import { useRef, useState } from 'react';
import { downloadPhoto, downloadAll, isSameOrigin } from '../../../utils/download';
import PhotoLightbox from './PhotoLightbox';

/**
 * Ceremony photo section: an Arabic "photos will be available soon" message
 * when there are none yet (the API's `photos` key can be entirely absent —
 * `whenLoaded('photos')` — not just an empty array, so this always guards
 * with `photos?.length`), otherwise a responsive grid of tiles that open a
 * fullscreen lightbox.
 *
 * By explicit user instruction, the empty state shows ONLY the message —
 * no section title and no download button, since there's nothing to
 * download and nothing to title yet.
 *
 * Download behavior is origin-aware (see src/utils/download.js): a
 * cross-origin `download` attribute is silently ignored by the browser and
 * navigates the tab to the raw image instead, losing the guest's
 * access_key from the address bar. So "تحميل الكل" only ever appears when
 * every photo is same-origin; per-photo controls fall back to "فتح الصورة"
 * (open in a new tab) for any photo that isn't.
 */
export default function PhotoGallery({ photos }) {
  const [loaded, setLoaded] = useState({});
  const [broken, setBroken] = useState({});
  const [lightboxIndex, setLightboxIndex] = useState(null);
  // Tile DOM nodes, keyed by index — passed to PhotoLightbox so it can
  // restore focus to the exact tile that opened it. Not derived from
  // document.activeElement on mount: Safari doesn't move focus to a
  // <button> on click, so that would silently fail there.
  const tileRefs = useRef({});

  if (!photos?.length) {
    return (
      <section className="ceremony-page__details" aria-label="صور الحفل">
        <div className="info-card photo-gallery__empty" style={{ '--delay': '90ms' }}>
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" aria-hidden="true" focusable="false">
            <rect x="3" y="4.5" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="8.5" cy="9.5" r="1.5" stroke="currentColor" strokeWidth="1.5" />
            <path
              d="M4 16.5l4.5-4.5a2 2 0 0 1 2.8 0L15 15.5M14 14l1.6-1.6a2 2 0 0 1 2.8 0L21 15.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="photo-gallery__empty-value">ستكون متاحة قريباً</span>
        </div>
      </section>
    );
  }

  const allSameOrigin = photos.every((p) => isSameOrigin(p.url));

  return (
    <section className="ceremony-page__details photo-gallery" aria-label="صور الحفل">
      <div className="photo-gallery__header">
        <h2 className="photo-gallery__title">صور الحفل</h2>
        {photos.length > 1 && allSameOrigin && (
          <button
            type="button"
            className="ceremony-error__retry photo-gallery__download-all"
            onClick={() => downloadAll(photos)}
          >
            تحميل الكل
          </button>
        )}
      </div>

      <ul className="photo-gallery__grid">
        {photos.map((photo, index) => {
          const isBroken = broken[index];
          const isLoaded = loaded[index];
          const sameOrigin = isSameOrigin(photo.url);

          return (
            <li
              key={photo.url || index}
              className="photo-gallery__item"
              style={{ '--delay': `${index * 60}ms` }}
            >
              <button
                type="button"
                ref={(el) => {
                  tileRefs.current[index] = el;
                }}
                className="photo-gallery__tile"
                onClick={() => !isBroken && setLightboxIndex(index)}
                aria-label={`عرض صورة رقم ${index + 1} من ${photos.length}`}
                disabled={isBroken}
              >
                {!isLoaded && !isBroken && <span className="photo-gallery__shimmer" aria-hidden="true" />}
                {isBroken ? (
                  <span className="photo-gallery__broken" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" focusable="false">
                      <rect x="3" y="4.5" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="1.5" />
                      <path
                        d="M3 19l5-5a2 2 0 0 1 2.8 0L14 17M4.5 4.5l15 15"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                ) : (
                  <img
                    className="photo-gallery__img"
                    src={photo.url}
                    alt={`صورة من الحفل رقم ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    style={{ opacity: isLoaded ? 1 : 0 }}
                    onLoad={() => setLoaded((prev) => ({ ...prev, [index]: true }))}
                    onError={() => setBroken((prev) => ({ ...prev, [index]: true }))}
                  />
                )}
              </button>

              {!isBroken && (
                <button
                  type="button"
                  className="photo-gallery__action"
                  onClick={(event) => {
                    event.stopPropagation();
                    downloadPhoto(photo);
                  }}
                  aria-label={sameOrigin ? 'تحميل الصورة' : 'فتح الصورة'}
                >
                  {sameOrigin ? (
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
                      <path
                        d="M12 3v12m0 0l-4-4m4 4l4-4M4 19h16"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
                      <path
                        d="M14 5h5v5M19 5l-8 8M9 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-3"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
          triggerElement={tileRefs.current[lightboxIndex]}
        />
      )}
    </section>
  );
}
